import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import moment from 'moment';
import {NavLink} from 'react-router-dom';
import toast from 'react-hot-toast';
import {AiFillDelete, AiFillEdit} from 'react-icons/ai';
import {IoMdEye} from 'react-icons/io';
import {FaUserAlt} from 'react-icons/fa';

import {useDeleteJobMutation, useGetAllJobsQuery} from '../api/employer';
import CustomTable from '../components/common/CustomTable';
import {DebounceInput} from '../components/ui/InputField';
import {pageRoutes} from '../routes';
import {invalidateQueryV2} from '../services/reactQuery';
import AlertModal from '../components/AlertModal';
import {jobKeys} from '../services/reactQuery/cacheKey';

const ManageJob = () => {
  const [formData, setFormData] = useState({
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
    isDeleted: false,
    status: true,
  });
  const [removeJobId, setRemoveJobId] = useState(null);

  const {isLoading: isRemoveJobLoading, mutate: deleteJobMutation} =
    useDeleteJobMutation();

  const {data: jobData, isFetching: isJobsLoading} =
    useGetAllJobsQuery(formData);
  const {
    jobData: allJobs = [],
    totalItems = null,
    totalPages = null,
  } = jobData?.data || {jobData: []};

  const {handleSubmit, watch, control} = useForm({
    mode: 'all',
  });

  const handleFormSubmission = async (values) => {
    const {search} = values;

    // set pagination to page 1
    setFormData((prev) => ({
      ...prev,
      page: 1,
      search,
    }));
  };

  const handleDeleteJob = () => {
    if (!removeJobId) return;

    deleteJobMutation(removeJobId, {
      onSuccess: () => {
        setRemoveJobId(null);
        toast.success('Job successfully removed.');
        invalidateQueryV2(jobKeys.all);
      },
      onError: (data) => toast.error(data.response.data.message),
    });
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  const tHeadData = [
    {
      id: 1,
      keyIndex: 'title',
      label: 'Job Title',
      sort: true,
      width: '18%',
    },
    {
      id: 2,
      keyIndex: 'vacancies',
      label: 'Vacancy',
      sort: true,
      width: '10%',
    },
    {
      id: 3,
      keyIndex: 'employer',
      label: 'Employer',
      sort: true,
      width: '15%',
    },
    {
      id: 4,
      keyIndex: 'inbox',
      label: 'Inbox',
    },
    {
      id: 5,
      keyIndex: 'inProgress',
      label: 'In Progress',
    },
    {
      id: 6,
      keyIndex: 'hired',
      label: 'Hired',
    },
    {
      id: 7,
      keyIndex: 'rejected',
      label: 'Rejected',
    },
    {
      id: 8,
      keyIndex: 'createdAt',
      label: 'Created Date',
      sort: true,
      width: '15%',
      render: (item) => <>{moment(item).format('MM/DD/YYYY h:mm a')}</>,
    },
    {
      id: 9,
      keyIndex: '_id',
      label: 'Action',
      width: '10%',
      render: (_, {_id}) => (
        <div className="flex gap-2">
          <NavLink
            aria-label="show candidat details"
            to={`${pageRoutes.manageJob}/${_id}`}
          >
            <IoMdEye size={23} />
          </NavLink>

          <NavLink aria-label="show candidat details" to={`/edit-job/${_id}`}>
            <AiFillEdit size={20} />
          </NavLink>

          <NavLink
            aria-label="job management"
            to={`${pageRoutes.jobManagement}/${_id}`}
          >
            <FaUserAlt size={18} />
          </NavLink>

          <button
            type="button"
            aria-label="remove candidate from job"
            onClick={() => setRemoveJobId(_id)}
          >
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <AlertModal
        type="warning"
        isOpen={removeJobId}
        onClose={() => setRemoveJobId(null)}
        title="Confirm this action"
        onCancel={() => setRemoveJobId(null)}
        onOK={() => handleDeleteJob()}
        okLoading={isRemoveJobLoading}
      >
        Are you sure you want to delete this candidate?
      </AlertModal>

      <div className="px-8 py-6 flex flex-col gap-5">
        <div className="flex items-center gap-2">
          <h3 className="text-2xl font-bold">
            Jobs {!!Number(totalItems) && `(${totalItems})`}
          </h3>
        </div>
        <form>
          <div className="flex justify-between">
            <DebounceInput
              control={control}
              name="search"
              placeholder="Search"
            />
          </div>
        </form>

        <div className="bg-[white] p-10 border">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={allJobs}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isJobsLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ManageJob;
